import type { Rental } from '../lib/rental';
import useStore from '../lib/store';
import LazyRentalCard from './LazyRentalCard';
import { Button } from './ui/Button';
import { ArrowPathIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { titleCaseLocation } from '../lib/utils';

export function RentalList({
  rentals,
  status,
  wide,
  onCreateAlert,
  showCreateAlertButton = true,
  showLikedOnly = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  totalMatches,
  isMapScoped = false,
  selectedLocation = null,
  errorMessage,
}: {
  rentals: Rental[],
  status: string,
  wide: boolean,
  onCreateAlert?: () => void,
  showCreateAlertButton?: boolean,
  showLikedOnly?: boolean,
  hasMore?: boolean,
  loadingMore?: boolean,
  onLoadMore?: () => void,
  totalMatches?: number | null,
  isMapScoped?: boolean,
  selectedLocation?: string | null,
  errorMessage?: string,
}) {
  const mapBounds = useStore((state) => state.map.bounds);
  const matchCount = rentals.length;
  const canShowTotalMatches = totalMatches !== null && totalMatches !== undefined;
  const totalMatchCount = canShowTotalMatches ? totalMatches : matchCount;
  const formattedMatchCount = matchCount.toLocaleString();
  const formattedTotalMatchCount = totalMatchCount.toLocaleString();
  const matchCountLabel = canShowTotalMatches ? `${formattedMatchCount} of ${formattedTotalMatchCount}` : formattedMatchCount;
  const rentalLabel = `rental${totalMatchCount !== 1 ? 's' : ''}`;
  const gridColumns = wide ? 'sm:grid-cols-3 md:grid-cols-4' : 'sm:grid-cols-2 md:grid-cols-3';
  const isRefreshing = status === 'loading' && rentals.length > 0;

  if (status === 'loading' && rentals.length === 0) {
    return <p className="text-center text-gray-500">Loading rentals...</p>;
  }

  if (status === 'no-location') {
    return <p className="text-center text-red-500">Location not found.</p>;
  }

  if (status === 'error') {
    return <p className="text-center text-red-500">{errorMessage || 'Error loading rentals.'}</p>;
  }

  if (status !== 'loaded' && status !== 'loading') {
    return null;
  }

  if (rentals.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 text-center text-gray-500">
        <p>No rentals found.</p>
        {hasMore && onLoadMore && (
          <Button
            type="button"
            variant="secondary"
            onClick={onLoadMore}
            disabled={loadingMore || status === 'loading'}
          >
            {loadingMore || status === 'loading' ? 'Loading...' : 'Load more rentals'}
          </Button>
        )}
        {onCreateAlert && (
          <Button
            type="button"
            onClick={onCreateAlert}
            className="inline-flex items-center gap-2 bg-orange-600 text-white hover:bg-orange-700 focus:ring-orange-300"
          >
            <EnvelopeIcon className="h-4 w-4" />
            Get emailed when one appears
          </Button>
        )}
      </div>
    );
  }

  return (
    <div id="rental-list">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="inline-flex items-center gap-2 text-gray-700 font-medium">
          {isRefreshing && (
            <span className="inline-flex shrink-0 items-center gap-1 text-sm font-normal text-blue-600">
              <ArrowPathIcon aria-hidden="true" className="h-4 w-4 animate-spin" />
              Updating
            </span>
          )}
          <span>
            {showLikedOnly
              ? `Showing ${matchCount} liked rental${matchCount !== 1 ? 's' : ''}`
              : selectedLocation
                ? `Showing ${matchCountLabel} ${rentalLabel} at ${titleCaseLocation(selectedLocation)}`
                : mapBounds && isMapScoped
                  ? `Showing ${matchCountLabel} ${rentalLabel} in this map view`
                  : `Showing ${matchCountLabel} latest ${rentalLabel}`}
          </span>
        </div>
        {onCreateAlert && showCreateAlertButton && (
          <Button
            type="button"
            onClick={onCreateAlert}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap bg-orange-600 text-white hover:bg-orange-700 focus:ring-orange-300"
          >
            <EnvelopeIcon className="h-4 w-4" />
            Get email alerts
          </Button>
        )}
      </div>
      <div className={`grid grid-cols-1 ${gridColumns} gap-6 items-stretch`}>
        {rentals.map((rental, index) => {
          return <LazyRentalCard key={rental.id} rental={rental} priority={index < 6} />;
        })}
      </div>
      {hasMore && onLoadMore && (
        <div className="mt-6 flex justify-center">
          <Button
            type="button"
            variant="secondary"
            onClick={onLoadMore}
            disabled={loadingMore || status === 'loading'}
          >
            {loadingMore || status === 'loading' ? 'Loading...' : 'Load more rentals'}
          </Button>
        </div>
      )}
    </div>
  );
}
